import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Gauge, AlertTriangle, UserPlus, PhoneCall, ChevronRight, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';

const STANDARD_LIMIT = 3;

const toneCls = {
  alert: 'text-[#C0392B]',
  info: 'text-[#1D6FD1]',
  neutral: 'text-[#3C3C3C]',
};

const actionMeta = {
  assegnare: { Icon: AlertTriangle, cls: 'text-[#C0392B]' },
  followup: { Icon: PhoneCall, cls: 'text-[#C0392B]' },
  nuovo: { Icon: UserPlus, cls: 'text-[#1D6FD1]' },
};

const Indicator = ({ ind }) => (
  <div className="min-w-0 px-2.5 py-2.5" data-testid={`control-indicator-${ind.id}`}>
    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 leading-tight break-words">{ind.label}</p>
    <p className={`text-xl font-bold mt-1 ${toneCls[ind.tone] || toneCls.neutral}`}>{ind.count}</p>
    {ind.subtitle ? <p className="text-[10px] text-gray-400 mt-0.5 leading-tight break-words">{ind.subtitle}</p> : null}
  </div>
);

const PriorityRow = ({ row }) => {
  const meta = actionMeta[row.action_type] || actionMeta.nuovo;
  const Icon = meta.Icon;
  // Densità (§11): evita di ripetere la stessa informazione. Se lo stage coincide con
  // l'azione, mostra solo la responsabilità nella riga secondaria.
  const dupe = row.stage_label && row.action_label &&
    row.stage_label.trim().toLowerCase() === row.action_label.trim().toLowerCase();
  const showStage = row.stage_label && !dupe;
  return (
    <Link to={row.link || '#'} data-testid={`control-priority-${row.lead_id}`}
      className="flex items-center justify-between p-2.5 rounded-lg hover:bg-gray-50 transition-colors group no-underline text-inherit">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{row.client_name}</p>
        {(showStage || row.responsibility) && (
          <p className="text-xs text-gray-500 truncate">
            {showStage ? <span className="font-medium">{row.stage_label}</span> : null}
            {showStage && row.responsibility ? <span className="text-gray-400"> · </span> : null}
            {row.responsibility ? <span className="text-gray-400">{row.responsibility}</span> : null}
          </p>
        )}
        <p className={`text-xs flex items-center gap-1 mt-0.5 ${meta.cls}`}>
          <Icon className="w-3 h-3 shrink-0" />
          <span className="truncate">{row.action_label}</span>
        </p>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#FF7819] transition-colors shrink-0" />
    </Link>
  );
};

export const ControlloCommerciale = ({ control = {}, onExpandChange }) => {
  const { indicators = [], priorities = [], view_all_link = '/leads' } = control;
  const [expanded, setExpanded] = useState(false);

  const canExpand = priorities.length > STANDARD_LIMIT;
  const shown = expanded ? priorities : priorities.slice(0, STANDARD_LIMIT);
  const hiddenCount = priorities.length - STANDARD_LIMIT;

  useEffect(() => { onExpandChange && onExpandChange(expanded && canExpand); }, [expanded, canExpand, onExpandChange]);

  return (
    <Card className="bg-white rounded-xl border border-[#E4E4E4] shadow-sm h-full flex flex-col" data-testid="controllo-commerciale">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Gauge className="w-5 h-5 text-[#FF7819]" />
          Controllo commerciale
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 flex flex-col flex-1">
        {/* 4 mini-indicatori: Lead nuovi · Da assegnare · Da seguire · Attese scadute */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#E4E4E4] rounded-lg overflow-hidden border border-[#E4E4E4]" data-testid="control-indicators">
          {indicators.map((ind) => <div key={ind.id} className="bg-white"><Indicator ind={ind} /></div>)}
        </div>

        {/* Priorità (max 3 in standard) */}
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mt-4 mb-1.5" data-testid="control-priority-title">Priorità</p>
        {priorities.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-center flex-1" data-testid="control-empty">
            <div className="w-11 h-11 bg-green-50 rounded-full flex items-center justify-center mb-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-sm font-medium text-gray-600">Nessuna priorità commerciale</p>
          </div>
        ) : (
          <div className="space-y-0.5" data-testid="control-priority-list">
            {shown.map((row) => <PriorityRow key={row.lead_id} row={row} />)}
          </div>
        )}

        {canExpand && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid="control-priority-toggle"
            className="mt-1.5 text-xs font-medium text-[#FF7819] hover:underline flex items-center gap-1 self-start"
          >
            {expanded ? (<>Riduci <ChevronUp className="w-3.5 h-3.5" /></>) : (<>Mostra altre {hiddenCount} <ChevronDown className="w-3.5 h-3.5" /></>)}
          </button>
        )}

        <div className="mt-3 pt-2 border-t border-gray-100">
          <Link to={view_all_link} data-testid="control-view-all"
            className="text-xs font-medium text-[#FF7819] hover:underline flex items-center gap-1">
            Vedi tutte le opportunità <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};
